<template>
  <div id="pageNotificaciones">
    <v-container fluid>
      <v-row>
        <v-col>
          <v-card class="notification-card">
            <v-toolbar color="primary" dark flat>
              <v-icon class="mr-2">mdi-bell-outline</v-icon>
              <v-toolbar-title>Notificaciones</v-toolbar-title>
              <v-spacer></v-spacer>
              <!-- Badge con contador de no leídas -->
              <v-badge
                :content="unreadCount"
                :value="unreadCount > 0"
                color="error"
                overlap
                class="mr-3"
              >
                <v-btn
                  small
                  outlined
                  color="white"
                  @click="markAllAsRead"
                  :disabled="unreadCount === 0 || loadingItems"
                >
                  <v-icon left small>mdi-check-all</v-icon>
                  Marcar todo como leído
                </v-btn>
              </v-badge>
              <v-btn
                icon
                class="mr-2"
                @click="refreshDataTable()"
                :disabled="loadingItems"
                title="Actualizar notificaciones"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>            </v-toolbar>
            
            <!-- Barra de filtros -->
            <v-card-text class="py-2 px-4">
              <div class="d-flex align-center flex-wrap filter-row-inner">
                <!-- Filtros por condición -->
                <template v-if="hasVehicleConditionPermissions">
                  <v-chip
                    v-if="DEALER_HAS_TYPE('vn') && HAS_PERMISSION('vn')"
                    small
                    color="green lighten-4"
                    text-color="green darken-3"
                    :class="['mr-1', {'condition-off': filter.vehicle_condition && filter.vehicle_condition.length > 0 && !filter.vehicle_condition.includes('new')}]"
                    @click="applyConditionFilter('new')"
                  >
                    <v-icon left x-small>mdi-car-limousine</v-icon>
                    VN
                  </v-chip>
                  <v-chip
                    v-if="DEALER_HAS_TYPE('vo') && HAS_PERMISSION('vo')"
                    small
                    color="amber lighten-4"
                    text-color="amber darken-4"
                    :class="['mr-1', {'condition-off': filter.vehicle_condition && filter.vehicle_condition.length > 0 && !filter.vehicle_condition.includes('used')}]"
                    @click="applyConditionFilter('used')"
                  >
                    <v-icon left x-small>mdi-car-estate</v-icon>
                    VO
                  </v-chip>
                  <v-chip
                    v-if="DEALER_HAS_TYPE('taller') && HAS_PERMISSION('taller')"
                    small
                    color="red lighten-4"
                    text-color="red darken-3"
                    :class="['mr-1', {'condition-off': filter.vehicle_condition && filter.vehicle_condition.length > 0 && !filter.vehicle_condition.includes('taller')}]"
                    @click="applyConditionFilter('taller')"
                  >
                    <v-icon left x-small>mdi-tools</v-icon>
                    Taller
                  </v-chip>
                  <v-divider vertical class="mx-2 my-1"></v-divider>
                </template>

                <!-- Estado de lectura -->
                <v-chip
                  small
                  :color="readFilter === 'noleidas' ? 'blue lighten-4' : ''"
                  :text-color="readFilter === 'noleidas' ? 'blue darken-3' : 'grey darken-1'"
                  :outlined="readFilter !== 'noleidas'"
                  class="mr-1"
                  @click="readFilter = 'noleidas'"
                >
                  No leídas
                </v-chip>
                <v-chip
                  small
                  :color="readFilter === 'leidas' ? 'blue lighten-4' : ''"
                  :text-color="readFilter === 'leidas' ? 'blue darken-3' : 'grey darken-1'"
                  :outlined="readFilter !== 'leidas'"
                  class="mr-1"
                  @click="readFilter = 'leidas'"
                >
                  Leídas
                </v-chip>
                <v-chip
                  small
                  :color="readFilter === 'todas' ? 'blue lighten-4' : ''"
                  :text-color="readFilter === 'todas' ? 'blue darken-3' : 'grey darken-1'"
                  :outlined="readFilter !== 'todas'"
                  class="mr-1"
                  @click="readFilter = 'todas'"
                >
                  Todas
                </v-chip>
                <v-divider vertical class="mx-2 my-1"></v-divider>

                <!-- Tipo de notificación (multiselección) -->
                <v-menu v-model="typeMenu" offset-y bottom rounded="lg" :close-on-content-click="false" max-height="420">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      outlined
                      height="40"
                      :color="typeFilter.length ? 'primary' : 'grey darken-1'"
                      class="text-none mr-2 type-filter-btn"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon left small>mdi-filter-variant</v-icon>
                      Tipo de notificación
                      <v-chip v-if="typeFilter.length" x-small color="primary" text-color="white" class="ml-2 px-2">
                        {{ typeFilter.length }}
                      </v-chip>
                      <v-icon right small>mdi-chevron-down</v-icon>
                    </v-btn>
                  </template>
                  <v-card flat min-width="300" class="pb-1">
                    <div class="d-flex align-center justify-space-between px-3 py-2">
                      <span class="caption grey--text">Filtrar por tipo</span>
                      <v-btn x-small text color="primary" @click="typeFilter = []">Limpiar</v-btn>
                    </div>
                    <v-divider></v-divider>
                    <v-list dense class="py-1">
                      <v-list-item
                        v-for="t in availableTypeOptions"
                        :key="t.value"
                        class="px-3 type-filter-item"
                        @click="toggleTypeFilter(t.value)"
                      >
                        <v-list-item-action class="my-0 mr-2">
                          <v-checkbox
                            :input-value="typeFilter.includes(t.value)"
                            dense
                            hide-details
                            class="ma-0 pa-0"
                            @click.stop="toggleTypeFilter(t.value)"
                          ></v-checkbox>
                        </v-list-item-action>
                        <v-avatar size="24" :color="t.color + ' lighten-4'" class="mr-2">
                          <v-icon :color="t.color" x-small>{{ t.icon }}</v-icon>
                        </v-avatar>
                        <v-list-item-title class="body-2">{{ t.text }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-menu>

                <!-- Búsqueda -->
                <v-text-field
                  v-model="filter.search"
                  placeholder="Buscar matrícula, bastidor..."
                  prepend-inner-icon="mdi-magnify"
                  hide-details
                  clearable
                  dense
                  outlined
                  class="flex-grow-1"
                  style="max-width: 280px; min-width: 180px;"
                ></v-text-field>

                <v-btn v-if="hasActiveFilters" x-small text color="primary" class="ml-2" @click="resetFilters">
                  <v-icon left small>mdi-filter-remove</v-icon>
                  Limpiar filtros
                </v-btn>
              </div>
            </v-card-text>

            <v-divider></v-divider>
            <!-- Listado agrupado por vehículo -->
            <v-card-text class="pa-3 grouped-body">
              <v-row v-if="loadingItems">
                <v-col cols="12">
                  <v-skeleton-loader
                    type="list-item-avatar-two-line"
                    class="mb-3"
                    v-for="n in 4"
                    :key="`skeleton-${n}`"
                  ></v-skeleton-loader>
                </v-col>
              </v-row>
              <div v-else-if="!groupedVehicles.length" class="text-center py-10">
                <v-icon size="64" color="grey lighten-2" class="mb-3">mdi-bell-off-outline</v-icon>
                <h3 class="text-h5 grey--text text--darken-1">No hay notificaciones</h3>
                <p class="text-subtitle-1 grey--text">No se encontraron notificaciones que coincidan con los filtros seleccionados</p>
              </div>
              <template v-else>
                <v-card
                  v-for="group in groupedVehicles"
                  :key="group.key"
                  outlined
                  class="vehicle-group-card mb-2"
                >
                  <!-- Cabecera del vehículo -->
                  <div class="vehicle-header d-flex align-center px-4 py-3" @click="toggleCard(group.key)">
                    <v-chip label small color="blue darken-3" text-color="white" class="font-weight-bold mr-2 plate-chip">
                      {{ group.vehicle ? (group.vehicle.plate_number || group.vehicle.iic_number || 'S/M') : 'OTRAS' }}
                    </v-chip>
                    <v-chip
                      v-if="group.vehicle && group.vehicle.condition"
                      x-small
                      :color="getConditionColor(group.vehicle.condition)"
                      :text-color="getConditionTextColor(group.vehicle.condition)"
                      class="mr-3"
                    >
                      {{ getConditionLabel(group.vehicle.condition) }}
                    </v-chip>
                    <div class="flex-grow-1" style="min-width: 0">
                      <div class="subtitle-2 font-weight-medium">
                        {{ group.vehicle ? `${group.vehicle.brand || ''} ${group.vehicle.model || ''}` : 'Notificaciones generales' }}
                      </div>
                      <div v-if="group.vehicle && (group.vehicle.frame_number || group.vehicle.iic_number)" class="caption grey--text d-flex align-center flex-wrap">
                        <v-icon x-small class="mr-1">mdi-barcode</v-icon>
                        <span class="mr-2 vin-number">{{ group.vehicle.frame_number || group.vehicle.iic_number }}</span>
                        <v-btn
                          x-small
                          text
                          :color="copiedVin === group.key ? 'success' : 'grey darken-1'"
                          class="px-1 text-none"
                          @click.stop="copyVin(group)"
                        >
                          <v-icon x-small left>{{ copiedVin === group.key ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                          {{ copiedVin === group.key ? 'Copiado' : 'Copiar' }}
                        </v-btn>
                      </div>
                    </div>
                    <v-chip v-if="group.unread > 0" x-small color="error" text-color="white" class="mr-1">
                      <v-icon x-small left>mdi-bell-ring</v-icon>
                      {{ group.unread }}
                    </v-chip>
                    <v-chip x-small outlined class="mr-2">{{ group.total }}</v-chip>
                    <v-icon class="chevron" :class="{'chevron-open': isCardOpen(group.key)}">mdi-chevron-down</v-icon>
                  </div>

                  <v-expand-transition>
                    <div v-show="isCardOpen(group.key)">
                      <v-divider></v-divider>
                      <!-- Resumen por tipo -->
                      <div class="px-4 py-2 grey lighten-5 d-flex align-center flex-wrap">
                        <v-chip
                          v-for="(count, typeKey) in group.typeCounts"
                          :key="typeKey"
                          x-small
                          :color="getTypeMeta(typeKey).color + ' lighten-4'"
                          :text-color="getTypeMeta(typeKey).color + ' darken-2'"
                          class="mr-1 my-1"
                        >
                          <v-icon x-small left>{{ getTypeMeta(typeKey).icon }}</v-icon>
                          {{ count }}
                        </v-chip>
                      </div>
                      <v-divider></v-divider>
                      <!-- Notificaciones del vehículo -->
                      <div>
                        <div
                          v-for="item in getPagedNotifs(group)"
                          :key="item.id"
                          class="notif-row d-flex px-4 py-2"
                          :class="{'notif-unread': !item.read_at}"
                          @click="viewNotificationDetails(item)"
                        >
                          <v-avatar size="30" :color="getNotificationTypeColor(item.type) + ' lighten-4'" class="mr-3 mt-1">
                            <v-icon :color="getNotificationTypeColor(item.type)" small>{{ getNotificationTypeIcon(item.type) }}</v-icon>
                          </v-avatar>
                          <div class="flex-grow-1" style="min-width: 0">
                            <div class="body-2 font-weight-medium">{{ getNotificationTitle(item) }}<v-chip v-if="item.vehicle_cedido" x-small label color="teal lighten-5" text-color="teal darken-3" class="ml-2"><v-icon x-small left>mdi-car-key</v-icon>Vehículo cedido</v-chip></div>
                            <div class="caption grey--text text--darken-1">{{ getNotificationContent(item) }}</div>
                            <div v-if="item.movement" class="caption grey--text d-flex align-center">
                              <v-icon x-small class="mr-1">mdi-subdirectory-arrow-right</v-icon>
                              {{ movementRefLabel(item) }} #{{ item.movement.id }} · {{ formatDate(item.movement.created_at, true) }}
                            </div>
                            <div class="d-flex align-center mt-1">
                              <v-chip
                                x-small
                                :color="item.read_at ? 'grey lighten-3' : 'blue lighten-4'"
                                :text-color="item.read_at ? 'grey darken-1' : 'blue darken-3'"
                                class="mr-2"
                              >
                                {{ item.read_at ? 'Leído' : 'No leído' }}
                              </v-chip>
                              <v-btn v-if="!item.read_at" x-small text color="primary" class="text-none" @click.stop="toggleReadStatus(item)">
                                Marcar leído
                              </v-btn>
                            </div>
                          </div>
                          <div class="caption grey--text ml-2 text-no-wrap">{{ formatDate(item.created_at) }}</div>
                        </div>
                      </div>
                      <!-- Paginación interna de la tarjeta -->
                      <template v-if="getGroupPageCount(group) > 1">
                        <v-divider></v-divider>
                        <div class="d-flex align-center justify-center py-1 grey lighten-5">
                          <v-pagination
                            :value="getGroupPage(group.key)"
                            :length="getGroupPageCount(group)"
                            :total-visible="7"
                            circle
                            class="group-pagination"
                            @input="setGroupPage(group.key, $event)"
                          ></v-pagination>
                          <span class="caption grey--text ml-2">{{ getGroupRangeText(group) }}</span>
                        </div>
                      </template>
                    </div>
                  </v-expand-transition>
                </v-card>

                <!-- Paginación -->
                <v-row>
                  <v-col cols="12" class="d-flex justify-center align-center flex-wrap pa-2">
                    <div class="text-center d-flex align-center mr-4">
                      <span class="text-body-2 grey--text text--darken-1">Filas por página:</span>
                      <v-select v-model="pagination.itemsPerPage" :items="footer.itemsPerPageOptions" dense outlined
                        hide-details class="ml-2" style="width: 80px;" @change="onItemsPerPageChange"></v-select>
                    </div>

                    <v-pagination v-model="pagination.page" :length="Math.ceil(totalItems / pagination.itemsPerPage)"
                      :total-visible="7" circle @input="onPageChange"></v-pagination>
                  </v-col>
                </v-row>
              </template>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <!-- Diálogo para ver detalles de la notificación -->
    <v-dialog v-model="detailDialog.visible" max-width="500">
      <v-card v-if="detailDialog.item">
        <v-card-title :class="['white--text', getNotificationTypeColor(detailDialog.item.type)]">
          <v-icon left color="white">{{ getNotificationTypeIcon(detailDialog.item.type) }}</v-icon>
          {{ getNotificationTitle(detailDialog.item) }}
          <v-chip v-if="detailDialog.item.vehicle_cedido" x-small label color="teal lighten-5" text-color="teal darken-3" class="ml-2"><v-icon x-small left>mdi-car-key</v-icon>Vehículo cedido</v-chip>
        </v-card-title>
        <v-card-text class="pa-4">
          <v-list dense class="pa-0">
            <v-list-item two-line>
              <v-list-item-content>
                <v-list-item-subtitle class="caption">Fecha</v-list-item-subtitle>
                <v-list-item-title>{{ formatDate(detailDialog.item.created_at) }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item two-line v-if="(detailDialog.item.type === 'vehicle' || detailDialog.item.type === 'damage_movement') && detailDialog.item.vehicle">
              <v-list-item-content>
                <v-list-item-subtitle class="caption">Vehículo</v-list-item-subtitle>
                <v-list-item-title>
                  {{ detailDialog.item.vehicle.brand }} {{ detailDialog.item.vehicle.model }} ({{ detailDialog.item.vehicle.version }})
                  <v-chip
                    v-if="detailDialog.item.type === 'damage_movement'"
                    small
                    color="deep-orange"
                    class="ml-2 white--text"
                    label
                  >
                    <v-icon left small>mdi-car-brake-alert</v-icon>
                    Vehículo dañado
                  </v-chip>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item two-line v-if="(detailDialog.item.type === 'vehicle' || detailDialog.item.type === 'damage_movement') && detailDialog.item.vehicle">
              <v-list-item-content>
                <v-list-item-subtitle class="caption">Matrícula</v-list-item-subtitle>
                <v-list-item-title>{{ detailDialog.item.vehicle.plate_number }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item two-line v-if="(detailDialog.item.type === 'vehicle' || detailDialog.item.type === 'damage_movement') && detailDialog.item.vehicle">
              <v-list-item-content>
                <v-list-item-subtitle class="caption">Estado</v-list-item-subtitle>
                <v-list-item-title>
                  <v-chip small :color="getStatusColor(detailDialog.item.vehicle.status)" dark>
                    {{ getStatusText(detailDialog.item.vehicle.status) }}
                  </v-chip>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item two-line v-if="detailDialog.item.user">
              <v-list-item-content>
                <v-list-item-subtitle class="caption">Usuario</v-list-item-subtitle>
                <v-list-item-title>
                  {{ detailDialog.item.user.name }} {{ detailDialog.item.user.lastname }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            color="grey darken-1"
            @click="detailDialog.visible = false"
          >
            Cerrar
          </v-btn>
          <v-btn
            text
            color="primary"
            @click="goToRelatedItem(detailDialog.item)"
          >
            Ver detalle completo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import NotificacionService from '../../services/NotificacionService'

export default {
  data: () => ({
    // Vista y carga
    loadingItems: false,
    totalItems: 0,
    tableId: 0,
    unreadCount: 0,
    readFilter: 'noleidas', // Filtro rápido: noleidas (por defecto) | leidas | todas
    typeFilter: [], // Tipos de notificación seleccionados (multiselección)
    typeMenu: false, // Menú desplegable de tipos
    openCards: [], // Tarjetas de vehículo expandidas (por defecto todas colapsadas)
    copiedVin: null, // Grupo cuyo bastidor se acaba de copiar
    groupPages: {}, // Página actual de notificaciones dentro de cada tarjeta de vehículo
    notifsPerGroup: 10, // Notificaciones por página dentro de cada tarjeta
    // Diálogo de detalles
    detailDialog: {
      visible: false,
      item: null
    },
    // Tabla
    footer: {
      itemsPerPageOptions: [10, 20, 30, 40, 50],
      itemsPerPageText: 'Notificaciones por página:',
      pageText: '{0}-{1} de {2}'
    },
    // Grupos de notificaciones por vehículo (respuesta del servidor)
    groups: [],
    // Filtros
    filter: {
      search: null,
      type: null,
      readStatus: null,
      vehicle_condition: []
    },
    currentFilter: {
      search: null,
      type: null,
      readStatus: null,
      vehicle_condition: []
    },
    error: false,
    // Opciones para el filtro de tipo de notificación
    notificationTypes: [
      { text: 'Vehículos', value: 'vehicle', icon: 'mdi-car', color: 'blue' },
      { text: 'Movimientos', value: 'movement', icon: 'mdi-truck-delivery', color: 'green' },
      { text: 'Fecha de movimiento modificada', value: 'movement_datetime_changed', icon: 'mdi-calendar-clock', color: 'orange' },
      { text: 'Alertas de movimiento', value: 'movement_alert', icon: 'mdi-alarm-light', color: 'amber' },
      { text: 'Movimiento con daños', value: 'damage_movement', icon: 'mdi-car-brake-alert', color: 'red' },
      { text: 'Daños VO – 1ª recepción', value: 'damage_first_reception_used', icon: 'mdi-car-brake-alert', color: 'red' },
      { text: 'Daños VN – 1ª recepción', value: 'damage_first_reception_new', icon: 'mdi-car-brake-alert', color: 'red' },
      { text: 'Falta equipamiento básico', value: 'equipment_missing', icon: 'mdi-package-variant-closed', color: 'deep-orange' }
    ]
  }),

  methods: {
    // Carga de datos (agrupados por vehículo desde el servidor)
    loadData () {
      // No cargar notificaciones si el usuario es admin
      if (this.CURRENT_USER && this.CURRENT_USER.roles && this.CURRENT_USER.roles.includes('admin')) {
        console.log('Usuario admin detectado - no se cargan notificaciones')
        this.loadingItems = false
        this.totalItems = 0
        this.unreadCount = 0
        return
      }
      
      this.loadingItems = true
      const params = {
        'page': this.pagination.page,
        'per-page': this.pagination.itemsPerPage,
        'filter[read]': this.readFilter === 'leidas' ? 'read' : this.readFilter === 'todas' ? 'all' : 'unread'
      }
      if (this.typeFilter.length > 0) {
        params['filter[types]'] = this.typeFilter
      }
      if (Array.isArray(this.filter.vehicle_condition) && this.filter.vehicle_condition.length > 0) {
        params['filter[vehicle_condition]'] = this.filter.vehicle_condition
      }
      if (this.filter.search) {
        params['filter[search]'] = this.filter.search
      }
      NotificacionService.getGroupedByVehicle(params).then(response => {
        const payload = (response.data && response.data.data && response.data.data.data) || {}
        this.groups = payload.items || []
        this.totalItems = (payload._meta && payload._meta.totalCount) || 0
        this.groupPages = {}
        if (payload.unread_total !== undefined) {
          this.unreadCount = payload.unread_total
        }
      }).catch(error => {
        console.error('Error al cargar notificaciones agrupadas:', error)
        this.$toast.error('Ocurrió un problema al intentar obtener las notificaciones')
      }).finally(() => {
        this.loadingItems = false
      })
    },
    debouncedLoadData: _.debounce(function () {
      this.loadData()
    }, 1000),
    refreshDataTable () {
      this.tableId++
      this.loadData()
    },
    async calculateUnreadCount () {
      const unread = await NotificacionService.unreadCount()
      console.log(unread.data.data.unread_count)
      
      this.unreadCount = unread.data.data.unread_count || 0
    },
    // Filtros
    commitFilter () {
      for (let [key, value] of Object.entries(this.filter)) {
        this.currentFilter[key] = value
      }
      this.pagination.page = 1
      this.loadData()
    },
    rollbackFilter () {
      for (let [key, value] of Object.entries(this.currentFilter)) {
        this.filter[key] = value
      }
    },
    updateFilterList () {
      // Método para actualizar listas de filtros si es necesario
    },
    // Acciones de notificaciones
    deleteNotificacion (item) {
      this.$confirm('¿Está seguro que desea eliminar esta notificación?').then(result => {
        if (result) {
          this.loadingItems = true
          this.$store.dispatch('DELETE_NOTIFICACIONES', item).then(() => {
            this.refreshDataTable()
            this.$toast.success('Notificación eliminada con éxito')
            this.loadingItems = false
          })
        }
      })
    },
    toggleReadStatus (item) {
      this.loadingItems = true
      const action = item.read_at ? 'UNREAD_NOTIFICATION' : 'READ_NOTIFICATION'
      this.$store.dispatch(action, item).then(() => {
        item.read_at = action === 'READ_NOTIFICATION' ? Date.now() : null
        this.calculateUnreadCount()
        this.loadingItems = false
        // Recargar la lista para mantenerla actualizada
        this.loadData()
      }).catch((error) => {
        console.error('Error en toggleReadStatus:', error)
        this.loadingItems = false
      })
    },
    markAllAsRead () {
      this.$confirm('¿Marcar todas las notificaciones como leídas?').then(async result => {
        if (result) {
          this.loadingItems = true
          await NotificacionService.readAll()
          this.calculateUnreadCount()
          this.loadingItems = false
          if (!this.error) {
            this.$toast.success('Todas las notificaciones marcadas como leídas')
            this.refreshDataTable()
          } else {
            this.$toast.error('Error al marcar todas las notificaciones como leídas')
          }
          this.error = false
        }
      })
    },
    viewNotificationDetails(item) {
      // Marcar como leída si no lo está
      if (!item.read_at && this.CURRENT_USER.roles.includes('logistics')) {
        this.$store.dispatch('READ_NOTIFICATION', item).then(() => {
          item.read_at = Date.now()
          this.calculateUnreadCount()
          // Recargar la lista para mantenerla actualizada
          this.loadData()
        })
      }
      // Si es una notificación de tipo movimiento, ir directamente a la página de detalles
      if ((item.type === 'movement' || item.type === 'movement_datetime_changed' || item.type === 'movement_alert') && item.movement && item.movement.id) {
        this.$router.push(this.movementRoute(item))
        return
      }
      // Si es una notificación de daño en movimiento, daños en 1ra recepción, o falta de equipamiento, ir a detalle del movimiento
      if (
        (item.type === 'damage_movement' || item.type === 'damage_first_reception_used' || item.type === 'damage_first_reception_new' || (item.type && item.type.startsWith('equipment_missing_'))) && item.movement && item.movement.id
      ) {
        this.$router.push(this.movementRoute(item))
        return
      }
      this.detailDialog.item = item
      this.detailDialog.visible = true
    },
    goToRelatedItem(item) {
      this.detailDialog.visible = false
      if (item.type === 'vehicle' && item.vehicle) {
        this.$router.push({ name: 'VehiculoDetail', params: { id: item.vehicle.id } })
      } else if ((item.type === 'movement' || item.type === 'movement_datetime_changed' || item.type === 'movement_alert') && item.movement) {
        this.$router.push(this.movementRoute(item))
      } else if (
        (item.type === 'damage_movement' || item.type === 'damage_first_reception_used' || item.type === 'damage_first_reception_new') && item.movement && item.movement.id
      ) {
        this.$router.push(this.movementRoute(item))
      }
    },
    // N-14: distinguir entre movimiento y cesión al rutear/etiquetar notificaciones.
    isAssignmentMovement (item) {
      return !!(item && item.movement && item.movement.type === 'assignment')
    },
    movementRoute (item) {
      if (this.isAssignmentMovement(item)) {
        return { name: 'CesionDetail', params: { cesion_id: item.movement.id } }
      }
      return { name: 'MovimientoDetail', params: { movimiento_id: item.movement.id } }
    },
    movementRefLabel (item) {
      return this.isAssignmentMovement(item) ? 'Cesión' : 'Movimiento'
    },
    // AL1: título y texto propios para las alertas de movimiento, en el
    // formato acordado («Alerta: movimiento [estado] · Marca Modelo (…)» y
    // «El movimiento [origen] → [destino] sigue en [estado] desde el [fecha].
    // Supera el periodo de alerta configurado ([tiempo]).»).
    movementStatusLabel (status) {
      const map = {
        unreported: 'no reportado',
        reported: 'reportado',
        processing: 'en proceso',
        completed: 'completado'
      }
      return map[status] || (status || '—')
    },
    buildMovementAlertTitle (item) {
      const statusLabel = this.movementStatusLabel(item.movement?.status)
      const vehicle = item.vehicle || {}
      const brandModel = `${vehicle.brand || ''} ${vehicle.model || ''}`.trim()
      const id = this.vehicleIdentifier(vehicle) || vehicle.frame_number || ''
      const idPart = id ? ` (${id})` : ''
      const name = brandModel || 'Vehículo'
      return `Alerta: movimiento ${statusLabel} · ${name}${idPart}`
    },
    formatAlertDate (ts) {
      if (!ts) return ''
      return new Intl.DateTimeFormat('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(ts * 1000))
    },
    buildMovementAlertText (item) {
      const m = item.movement || {}
      const src = m.source_location?.name || '—'
      const dst = m.destination_location?.name || '—'
      const statusLabel = this.movementStatusLabel(m.status)
      const since = m.status_changed_at ? this.formatAlertDate(m.status_changed_at) : ''
      const sincePart = since ? ` desde el ${since}` : ''
      const threshold = m.alert_threshold_hours
      const thresholdPart = threshold ? ` Supera el periodo de alerta configurado (${threshold} h).` : ''
      return `El movimiento ${src} → ${dst} sigue en ${statusLabel}${sincePart}.${thresholdPart}`
    },
    // Formato y presentación
    formatDate (timestamp, short) {
      if (!timestamp) return 'N/A'
      const date = new Date(timestamp * 1000)
      if (short) {
        return new Intl.DateTimeFormat('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit'
        }).format(date)
      }
      return new Intl.DateTimeFormat('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date)
    },
    getNotificationTypeIcon (type) {
      if (type && type.startsWith('equipment_missing_')) return 'mdi-package-variant-closed'
      const icons = {
        'vehicle': 'mdi-car',
        'movement': 'mdi-truck-delivery',
        'movement_datetime_changed': 'mdi-truck-delivery',
        'movement_alert': 'mdi-alarm-light',
        'damage_movement': 'mdi-car-brake-alert',
        'damage_first_reception_used': 'mdi-car-brake-alert',
        'damage_first_reception_new': 'mdi-car-brake-alert'
      }
      return icons[type] || 'mdi-bell'
    },
    getNotificationTypeColor (type) {
  if (type && type.startsWith('equipment_missing_')) return 'blue'
      const colors = {
        'vehicle': 'blue',
        'movement': 'green',
        'movement_datetime_changed': 'green',
        'movement_alert': 'amber',
        'damage_movement': 'blue',
        'damage_first_reception_used': 'blue',
        'damage_first_reception_new': 'blue'
      }
      return colors[type] || 'grey'
    },
    getNotificationTypeText (type) {
      if (type && type.startsWith('equipment_missing_')) return 'Falta equipamiento'
      const texts = {
        'vehicle': 'Vehículo',
        'movement': 'Movimiento',
        'movement_datetime_changed': 'Movimiento',
        'damage_movement': 'Vehículo',
        'damage_first_reception_used': 'Vehículo',
        'damage_first_reception_new': 'Vehículo' // igual que vehicle
      }
      return texts[type] || 'Desconocido'
    },
    getStatusColor (status) {
      const colors = {
        'pending': 'orange',
        'created': 'success',
        'in_transit': 'blue',
        'delivered': 'green',
        'cancelled': 'red'
      }
      return colors[status] || 'grey'
    },
    getStatusText (status) {
      const texts = {
        'pending': 'Pendiente',
        'created': 'Creado',
        'in_transit': 'En tránsito',
        'delivered': 'Entregado',
        'cancelled': 'Cancelado'
      }
      return texts[status] || 'Desconocido'
    },
    getMovementTypeColor (type) {
      return type === 'entry' ? 'green' : 'blue'
    },
    // CO-15: si el vehículo no tiene matrícula usamos el bastidor para que
    // los textos no muestren "(null)" ni paréntesis vacíos.
    vehicleIdentifier (vehicle) {
      if (!vehicle) return ''
      return vehicle.plate_number || vehicle.frame_number || ''
    },
    // N-06: nombre único del equipamiento en toda la aplicación.
    equipmentDisplayName (name) {
      if (!name) return ''
      const key = String(name).trim().toLowerCase().replace(/\s+/g, '_')
      const map = {
        'baliza_v16': 'Baliza V16',
        'chalecos': 'Chaleco reflectante',
        'chaleco': 'Chaleco reflectante',
        'chaleco_reflectante': 'Chaleco reflectante',
        'triangulo': 'Triángulo de emergencia',
        'triángulo': 'Triángulo de emergencia',
        'triangulo_emergencia': 'Triángulo de emergencia',
        'triangulo_de_emergencia': 'Triángulo de emergencia',
        'documentacion': 'Documentación',
        'documentación': 'Documentación',
        'cable_carga': 'Cable de carga',
        'cable_de_carga': 'Cable de carga',
        'gato': 'Gato'
      }
      return map[key] || name
    },
    // N-02: etapa en la que se generó el aviso, para distinguir un mismo aviso
    // repetido en la entrega y en la recepción
    stageSuffix (n) {
      const labels = { entrega: 'Entrega', recepcion: 'Recepción' }
      return n && labels[n.stage] ? ` · ${labels[n.stage]}` : ''
    },
    getNotificationTitle (item) {
      if (item.type && item.type.startsWith('equipment_missing_')) {
        const equip = item.type.replace('equipment_missing_', '')
        const equipLabel = this.equipmentDisplayName(equip)
        const id = this.vehicleIdentifier(item.vehicle)
        return (id ? `Falta equipamiento: ${equipLabel} (${id})` : `Falta equipamiento: ${equipLabel}`) + this.stageSuffix(item)
      }
      if (item.type === 'vehicle') {
        return item.vehicle ? `Vehículo: ${item.vehicle.brand} ${item.vehicle.model}` : 'Notificación de vehículo'
      } else if (item.type === 'movement') {
        if (!item.movement) return 'Notificación de movimiento'
        if (item.movement.type === 'assignment') return 'Cesión'
        return `Movimiento: ${item.movement.type === 'entry' ? 'Entrada' : 'Salida'}`
      } else if (item.type === 'movement_alert') {
        return this.buildMovementAlertTitle(item)
      } else if (item.type === 'movement_datetime_changed') {
        if (item.title) {
          return item.title
        }
        const vehicleInfo = this.vehicleIdentifier(item.vehicle) || (item.vehicle ? `${item.vehicle.brand} ${item.vehicle.model}` : '')
        return vehicleInfo ? `Fecha de movimiento modificada - ${vehicleInfo}` : 'Fecha de movimiento modificada'
      } else if (item.type === 'damage_movement') {
        return (item.vehicle ? `Daño en movimiento: ${item.vehicle.brand} ${item.vehicle.model}` : 'Notificación de daño en movimiento') + this.stageSuffix(item)
      } else if (item.type === 'damage_first_reception_used' || item.type === 'damage_first_reception_new') {
        if (!item.vehicle) return 'Daño en 1ª recepción'
        const id = this.vehicleIdentifier(item.vehicle)
        return `Daño en 1ª recepción: ${item.vehicle.brand} ${item.vehicle.model}${id ? ` (${id})` : ''}`
      }
      return 'Notificación'
    },
    getNotificationContent (item) {
      if (item.type && item.type.startsWith('equipment_missing_')) {
        const equip = item.type.replace('equipment_missing_', '')
        const equipLabel = this.equipmentDisplayName(equip)
        const id = this.vehicleIdentifier(item.vehicle)
        const label = item.vehicle && item.vehicle.plate_number ? `con matrícula ${id}` : (id ? `con bastidor ${id}` : '')
        return `El vehículo ${label} no tiene asignado el equipamiento: ${equipLabel}.`.replace('  ', ' ')
      }
      if (item.type === 'vehicle') {
        const id = this.vehicleIdentifier(item.vehicle)
        const idLabel = item.vehicle && item.vehicle.plate_number ? `con matrícula ${id}` : (id ? `con bastidor ${id}` : '')
        return `Se ha ${item.vehicle?.status === 'created' ? 'creado' : 'actualizado'} el vehículo ${item.vehicle?.brand} ${item.vehicle?.model} ${idLabel}.`.replace('  ', ' ')
      } else if (item.type === 'movement') {
        if (item.movement?.type === 'assignment') {
          return 'Se ha registrado una cesión.'
        }
        return `Se ha registrado un movimiento de ${item.movement?.type === 'entry' ? 'entrada' : 'salida'}.`
      } else if (item.type === 'movement_alert') {
        return this.buildMovementAlertText(item)
      } else if (item.type === 'movement_datetime_changed') {
        const vehicleInfo = this.vehicleIdentifier(item.vehicle) || (item.vehicle ? `${item.vehicle.brand} ${item.vehicle.model}` : 'un vehículo')
        return `Se ha modificado la fecha del movimiento del vehículo ${vehicleInfo}.`
      } else if (item.type === 'damage_movement') {
        const id = this.vehicleIdentifier(item.vehicle)
        return `Se ha reportado un daño en el vehículo ${item.vehicle?.brand} ${item.vehicle?.model}${id ? ` (${id})` : ''}.`
      } else if (item.type === 'damage_first_reception_used') {
        const id = this.vehicleIdentifier(item.vehicle)
        return `Vehículo usado ${item.vehicle?.brand} ${item.vehicle?.model} dañado en 1ª recepción${id ? ` (${id})` : ''}.`
      } else if (item.type === 'damage_first_reception_new') {
        const id = this.vehicleIdentifier(item.vehicle)
        return `Vehículo nuevo ${item.vehicle?.brand} ${item.vehicle?.model} dañado en 1ª recepción${id ? ` (${id})` : ''}.`
      }
      return 'Sin detalles adicionales.'
    },
    // Pagination methods
    onPageChange (page) {
      // Actualizar la página en la paginación
      this.pagination.page = page
      // Forzar la carga de datos inmediatamente después del cambio de página
      this.loadData()
    },
    onItemsPerPageChange (perPage) {
      // Actualizar items por página en la paginación
      this.pagination.itemsPerPage = perPage
      // Asegurarse de volver a la primera página al cambiar elementos por página
      this.pagination.page = 1
      // Forzar la carga de datos
      this.loadData()
    },
    // Métodos para filtros rápidos y agrupación por vehículo
    getTypeMeta (key) {
      return this.notificationTypes.find(t => t.value === key) || { text: 'Otro', value: key, icon: 'mdi-bell', color: 'grey' }
    },
    toggleTypeFilter (value) {
      const index = this.typeFilter.indexOf(value)
      if (index > -1) {
        this.typeFilter.splice(index, 1)
      } else {
        this.typeFilter.push(value)
      }
    },
    toggleCard (key) {
      const index = this.openCards.indexOf(key)
      if (index > -1) {
        this.openCards.splice(index, 1)
      } else {
        this.openCards.push(key)
      }
    },
    isCardOpen (key) {
      return this.openCards.includes(key)
    },
    // Paginación interna de notificaciones dentro de cada tarjeta
    getGroupPage (key) {
      return this.groupPages[key] || 1
    },
    setGroupPage (key, page) {
      this.$set(this.groupPages, key, page)
    },
    getGroupPageCount (group) {
      return Math.ceil(group.notifs.length / this.notifsPerGroup)
    },
    getPagedNotifs (group) {
      const page = this.getGroupPage(group.key)
      const start = (page - 1) * this.notifsPerGroup
      return group.notifs.slice(start, start + this.notifsPerGroup)
    },
    getGroupRangeText (group) {
      const page = this.getGroupPage(group.key)
      const start = (page - 1) * this.notifsPerGroup + 1
      const end = Math.min(page * this.notifsPerGroup, group.notifs.length)
      return `${start}-${end} de ${group.notifs.length}`
    },
    copyVin (group) {
      const vin = group.vehicle && (group.vehicle.frame_number || group.vehicle.iic_number)
      if (!vin || !navigator.clipboard) return
      navigator.clipboard.writeText(vin).then(() => {
        this.copiedVin = group.key
        setTimeout(() => { this.copiedVin = null }, 2000)
      })
    },
    getConditionLabel (condition) {
      const labels = { 'new': 'VN', 'used': 'VO', 'taller': 'Taller' }
      return labels[condition] || condition
    },
    getConditionColor (condition) {
      const colors = { 'new': 'green lighten-4', 'used': 'amber lighten-4', 'taller': 'red lighten-4' }
      return colors[condition] || 'grey lighten-3'
    },
    getConditionTextColor (condition) {
      const colors = { 'new': 'green darken-3', 'used': 'amber darken-4', 'taller': 'red darken-3' }
      return colors[condition] || 'grey darken-2'
    },
    applyConditionFilter(condition) {
      // Si el array no está inicializado, inicializarlo
      if (!Array.isArray(this.filter.vehicle_condition)) {
        this.filter.vehicle_condition = []
      }
      
      // Si la condición ya está en el array, quitarla
      const index = this.filter.vehicle_condition.indexOf(condition)
      if (index > -1) {
        this.filter.vehicle_condition.splice(index, 1)
      } else {
        // Si no está, agregarla
        this.filter.vehicle_condition.push(condition)
      }
      
      this.commitFilter()
    },
    resetFilters () {
      this.filter.search = null
      this.filter.type = null
      this.filter.vehicle_condition = []
      this.typeFilter = []
      this.readFilter = 'noleidas'
      this.commitFilter()
    }
  },

  computed: {
    ...mapGetters(['CURRENT_USER', 'HAS_PERMISSION', 'DEALER_HAS_TYPE']),
    
    // Paginación
    pagination: {
      get: function () {
        return this.$store.getters.NOTIFICACIONES_PAG
      },
      set: function (value) {
        this.$store.commit('SET_NOTIFICACIONES_PAG', value)
      }
    },
    searchTerm () {
      return this.filter.search
    },
    totalPages () {
      return Math.ceil(this.totalItems / this.pagination.itemsPerPage)
    },
    hasUnreadNotifications () {
      return this.unreadCount > 0
    },
    hasActiveFilters () {
      return this.typeFilter.length > 0 ||
             (Array.isArray(this.filter.vehicle_condition) && this.filter.vehicle_condition.length > 0)
    },
    // Tipos disponibles en el desplegable
    availableTypeOptions () {
      return this.notificationTypes
    },
    // Grupos de vehículos normalizados para la vista
    groupedVehicles () {
      return (this.groups || []).map(g => ({
        key: g.vehicle ? `v-${g.vehicle.id}` : 'sin-vehiculo',
        vehicle: g.vehicle || null,
        notifs: (g.notifications || []).map(n => ({ ...n, vehicle: g.vehicle || null })),
        unread: g.unread_count || 0,
        total: g.total_count || 0,
        typeCounts: g.type_counts || {}
      }))
    },
    hasVehicleConditionPermissions() {
      // Mostrar filtros si el usuario tiene al menos 1 tipo de vehículo activo en el dealer
      const types = ['vn', 'vo', 'taller']
      const count = types.reduce((acc, type) => {
        // Primero verificar si el dealer tiene el tipo, luego verificar permisos
        return acc + (this.DEALER_HAS_TYPE(type) && this.HAS_PERMISSION(type) ? 1 : 0)
      }, 0)
      return count >= 1
    }
  },
  watch: {
    readFilter () {
      this.pagination.page = 1
      this.loadData()
    },
    typeFilter () {
      this.pagination.page = 1
      this.loadData()
    },
    pagination: {
      handler: _.debounce(function (newVal, oldVal) {
        const relevantPropsChanged = ['page', 'itemsPerPage', 'sortBy', 'sortDesc'].some(
          prop => !_.isEqual(newVal[prop], oldVal?.[prop])
        )
        if (relevantPropsChanged && !this.loadingItems) {
          this.loadData()
        }
      }, 200), // pequeño delay
      deep: true
    },
    searchTerm () {
      this.pagination.page = 1
      this.debouncedLoadData()
    }
  },

  async created () {
    // No cargar notificaciones si el usuario es admin
    if (this.CURRENT_USER && this.CURRENT_USER.roles && this.CURRENT_USER.roles.includes('admin')) {
      console.log('Usuario admin detectado - no se cargan notificaciones')
      this.loadingItems = false
      return
    }
    
    this.loadData()
  }
}
</script>

<style scoped>
.notification-card {
  border-radius: 8px;
  overflow: hidden;
}

.notification-card-item {
  transition: box-shadow 0.2s ease-in-out, transform 0.2s ease;
  cursor: pointer;
  border-radius: 8px;
}

.notification-card-item:hover {
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1) !important;
  transform: translateY(-2px);
}

.unread-notification {
  border-left: 4px solid var(--v-primary-base) !important;
  background-color: #f5f9ff;
}

.action-column button {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.action-column button:hover {
  opacity: 1;
}

.search-field >>> .v-input__slot {
  min-height: 36px !important;
}

.empty-state {
  min-height: 200px;
}

/* Barra de filtros */
.filter-row-inner {
  gap: 4px;
}

.condition-off {
  opacity: 0.35;
}

.type-filter-btn {
  border-color: rgba(0, 0, 0, 0.38);
  font-weight: 400;
  letter-spacing: normal;
}

/* Tarjetas agrupadas por vehículo */
.vehicle-group-card {
  border-radius: 10px !important;
}

.vehicle-header {
  cursor: pointer;
  user-select: none;
}

.vehicle-header:hover {
  background-color: #f8fafc;
}

.plate-chip {
  letter-spacing: 0.5px;
}

.chevron {
  transition: transform 0.2s ease;
}

.chevron-open {
  transform: rotate(180deg);
}

.notif-row {
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
}

.notif-row:last-child {
  border-bottom: none;
}

.notif-row:hover {
  background-color: #fafafa;
}

.notif-unread {
  border-left: 3px solid var(--v-primary-base);
  background-color: #f5f9ff;
}

.group-pagination >>> .v-pagination__item,
.group-pagination >>> .v-pagination__navigation {
  height: 26px;
  min-width: 26px;
  font-size: 0.75rem;
  box-shadow: none;
}

/* Animación para nuevas notificaciones */
@keyframes highlight {
  0% {
    background-color: rgba(var(--v-primary-base), 0.1);
  }
  100% {
    background-color: transparent;
  }
}

.new-notification {
  animation: highlight 2s ease-in-out;
}
</style>
